/** Accordion rows that participate in the mobile bottom sheet (set `data-addon-sheet` on `<details>` in Liquid). */
const ADD_ON_MOBILE_SHEET_OPEN = 'details.add-ons-options__input[data-addon-sheet][open]';
const ADD_ON_OPTION_SHEET_DETAILS = 'details.add-ons-options__input[data-addon-sheet]';

if (!customElements.get('product-add-ons')) {
  customElements.define(
    'product-add-ons',
    class ProductAddOns extends HTMLElement {
      connectedCallback() {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.addButton = this.querySelector('.add-on__add-cart');
        this.sectionId = this.dataset.sectionId || '';
        this.formId = this.dataset.formId || '';
        // Theme customizer: combined line (a7e9662) vs separate add-on variants (pre-commit).
        // Read attribute explicitly — missing attr defaults to combined (matches schema default).
        const combineAttr = this.getAttribute('data-combine-addon-products');
        this.combineAddonProducts = combineAttr !== 'false';

        this.onRadioChange = this.onRadioChange.bind(this);
        this.onEngravingInput = this.onEngravingInput.bind(this); 
        this.onAddToCartClick = this.onAddToCartClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onThemeVariantChange = this.onThemeVariantChange.bind(this);
        this.onFormChange = this.onFormChange.bind(this);
        this.schedulePriceUpdate = this.schedulePriceUpdate.bind(this);
        this.onDropdownToggle = this.onDropdownToggle.bind(this);
        this.onViewportResize = this.onViewportResize.bind(this);
        this.onMobileBackdropClick = this.onMobileBackdropClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDropdownOptionClick = this.onDropdownOptionClick.bind(this);
        this.onPageShow = this.onPageShow.bind(this);

        this.addEventListener('change', this.onRadioChange);
        this.addEventListener('input', this.onEngravingInput);
        this.addButton?.addEventListener('click', this.onAddToCartClick);
        this.addEventListener('toggle', this.onDropdownToggle, true);
        this.addEventListener('click', this.onDropdownOptionClick);
        window.addEventListener('resize', this.onViewportResize);
        document.addEventListener('keydown', this.onDocumentKeydown);
        document.addEventListener('click', this.onDocumentClick);

        this.ensureMobileBackdrop();

        const form = this.getProductForm();
        form?.addEventListener('change', this.onFormChange);
        document.addEventListener('flu:product:variant-change', this.onThemeVariantChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.scopeRoot = this.closest('.product-page-wrapper') || document;
        this.productData = this.getProductData();
        this.hasValidationError = false;
        this.setBasePriceFromVariantId();
        this.observePriceChanges();
        this.refreshConditionalPanels();
        this.updateAllSummaries();
        this.updateButtonState();

        this.updatePrice();
        window.addEventListener('pageshow', this.onPageShow);
        this.syncMobileDropdownState();
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.onPageShow);
        this.variantChangeUnsubscriber?.();
        this.getProductForm()?.removeEventListener('change', this.onFormChange);
        document.removeEventListener('flu:product:variant-change', this.onThemeVariantChange);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('resize', this.onViewportResize);
        this.removeEventListener('toggle', this.onDropdownToggle, true);
        this.removeEventListener('click', this.onDropdownOptionClick);
        this.mobileBackdrop?.removeEventListener('click', this.onMobileBackdropClick);
        this.priceObserver?.disconnect();
        if (this.priceUpdateTimer) window.clearTimeout(this.priceUpdateTimer);
        this._suppressPriceObserver = false;
        this.classList.remove('add-on-mobile-sheet-active');
        const anyActive = document.querySelector('product-add-ons.add-on-mobile-sheet-active');
        document.body.classList.toggle('addon-dropdown-mobile-open', Boolean(anyActive));
      }

      onVariantChange(event) {
        const sid = event?.data?.sectionId;
        if (!sid || !this.sectionId || sid !== this.sectionId) return;
        this.setBasePriceFromVariant(event?.data?.variant);
        this.refreshConditionalPanels();
        this.updateAllSummaries();
        // basePriceCents and data-base-price are already up to date from
        // setBasePriceFromVariant; no need to re-read from the form.
        this.schedulePriceUpdate(false);
      }

      onThemeVariantChange(event) {
        this.setBasePriceFromVariant(event?.detail?.variant);
        this.schedulePriceUpdate(false);
      }

      /**
       * After cart navigation + Back, bfcache can restore the DOM while summary
       * text is empty; CSS hides .add-on-option-label-closed when a radio is
       * checked, so users see no label until summaries are rebuilt.
       */
      onPageShow() {
        this.refreshConditionalPanels();
        this.updateAllSummaries();
        this.syncMobileDropdownState();
        this.updatePrice();
        this.updateButtonState();
      }

      onFormChange() {
        // Form changes that aren't variant changes (e.g. quantity) should still
        // trigger a price refresh but don't need to re-read the variant JSON.
        this.schedulePriceUpdate(false);
      }

      schedulePriceUpdate(rereadVariant = true) {
        if (this.priceUpdateTimer) window.clearTimeout(this.priceUpdateTimer);
        this.priceUpdateTimer = window.setTimeout(() => {
          this.priceUpdateTimer = null;
          // Re-read the variant price from product JSON so data-base-price stays
          // accurate after theme-driven variant changes (MutationObserver path).
          if (rereadVariant) this.setBasePriceFromVariantId();
          this.updatePrice();
        }, 0);
      }

      getProductForm() {
        if (!this.formId) return null;
        return document.getElementById(this.formId);
      }

      getProductData() {
        const productJson = document.getElementById(`ProductJson-${this.sectionId}`);
        if (!productJson) return null;

        try {
          return JSON.parse(productJson.textContent || '{}');
        } catch (error) {
          return null;
        }
      }

      parseMoneyToCents(value) {
        const raw = String(value || '').trim();
        if (!raw) return NaN;

        const cleaned = raw.replace(/[^\d,.\-]/g, '');
        if (!cleaned) return NaN;

        const lastDot = cleaned.lastIndexOf('.');
        const lastComma = cleaned.lastIndexOf(',');
        let decimalSep = '';

        if (lastDot > -1 || lastComma > -1) {
          decimalSep = lastDot > lastComma ? '.' : ',';
        }

        let normalized = cleaned;
        if (decimalSep) {
          const thousandsSep = decimalSep === '.' ? ',' : '.';
          normalized = normalized.replace(new RegExp(`\\${thousandsSep}`, 'g'), '');
          if (decimalSep === ',') normalized = normalized.replace(',', '.');
        } else {
          normalized = normalized.replace(/[.,]/g, '');
        }

        const amount = Number.parseFloat(normalized);
        if (Number.isNaN(amount)) return NaN;
        return Math.round(amount * 100);
      }

      formatMoney(cents) {
        const format = window.theme?.moneyFormat || '${{amount}}';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholder);
        if (!match) return String(cents / 100);

        const token = match[1];
        // Always use 2 decimal places when value is zero so we show $0.00 not $0
        let precision = (Number(cents) === 0) ? 2 : 2;
        let thousands = ',';
        let decimal = '.';

        if (token === 'amount_no_decimals') {
          precision = (Number(cents) === 0) ? 2 : 0;
        } else if (token === 'amount_with_comma_separator') {
          thousands = '.';
          decimal = ',';
        } else if (token === 'amount_no_decimals_with_comma_separator') {
          precision = (Number(cents) === 0) ? 2 : 0;
          thousands = '.';
          decimal = ',';
        }

        const parts = (Number(cents) / 100).toFixed(precision).split('.');
        const major = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        const minor = parts[1] ? `${decimal}${parts[1]}` : '';

        return format.replace(placeholder, `${major}${minor}`);
      }

      getMainPriceCents() {
        if (Number.isFinite(this.basePriceCents)) return this.basePriceCents;

        if (this.setBasePriceFromVariantId()) return this.basePriceCents;

        const priceEl = this.getPriceElements()[0];
        const baseFromAttr = parseInt(priceEl?.dataset?.basePrice || '', 10);
        if (!Number.isNaN(baseFromAttr)) return baseFromAttr;

        const parsed = this.parseMoneyToCents(priceEl?.textContent || '');
        if (!Number.isNaN(parsed)) return parsed;

        return NaN;
      }

      getCurrentVariantId() {
        const variantInput = this.getProductForm()?.querySelector('[name="id"]');
        const variantId = parseInt(variantInput?.value || '', 10);
        return Number.isNaN(variantId) ? null : variantId;
      }

      setBasePriceFromVariant(variant) {
        const price = Number(variant?.price);
        if (!Number.isFinite(price)) return false;

        this.basePriceCents = price;
        this.getPriceElements().forEach((el) => {
          // Write to the span if present, otherwise to the element itself.
          const target = el.querySelector('[data-product-price]') || el;
          target.dataset.basePrice = String(price);
        });
        return true;
      }

      setBasePriceFromVariantId() {
        const variantId = this.getCurrentVariantId();
        const variants = this.productData?.variants;
        if (!variantId || !Array.isArray(variants)) return false;

        const variant = variants.find((item) => Number(item.id) === variantId);
        return this.setBasePriceFromVariant(variant);
      }

      getPriceElements() {
        const root = this.scopeRoot || this.closest('.product-page-wrapper') || document;
        // Try the standard Shopify price span first.
        let els = Array.from(root.querySelectorAll('[data-product-price]'));
        // If Globo Product Options (or another app) has replaced the inner span,
        // fall back to the price wrapper div itself so we can still write to it.
        if (!els.length) {
          els = Array.from(root.querySelectorAll('[data-gpo-product-unit-price]'));
        }
        return els;
      }

      observePriceChanges() {
        const priceNodes = this.getPriceElements();
        if (!priceNodes.length || typeof MutationObserver === 'undefined') return;

        this.priceObserver = new MutationObserver(() => {
          if (this._suppressPriceObserver) return;
          this.schedulePriceUpdate();
        });

        priceNodes.forEach((node) => {
          this.priceObserver.observe(node, {
            childList: true,
            characterData: true,
            subtree: true,
          });
        });
      }

      updateDisplayedPrice(mainPriceCents, totalCents) {
        const mainFormatted = this.formatMoney(mainPriceCents);
        const totalFormatted = this.formatMoney(totalCents);

        this._suppressPriceObserver = true;
        this.getPriceElements().forEach((el) => {
          el.innerHTML = mainFormatted;
        });
        // Use requestAnimationFrame so the observer suppression lifts after the
        // browser has processed the current mutations, preventing feedback loops.
        requestAnimationFrame(() => {
          this._suppressPriceObserver = false;
        });

        // Mirror the total into the ATC button price span
        const btnPriceEl = this.querySelector('.add-on__add-cart-price');
        if (btnPriceEl) {
          btnPriceEl.textContent = totalFormatted;
        }
      }

      updateButtonState() {
        const labelEl = this.querySelector('.add-on__add-cart-label');
        const btn = this.querySelector('.add-on__add-cart');
        if (!labelEl) return;

        // Check all REQUIRED visible add-on dropdowns/inputs
        const allRequiredSatisfied = this.allRequiredSatisfied();
        const isNotReady = !allRequiredSatisfied;

        if (allRequiredSatisfied) {
          labelEl.textContent = 'Add to Cart';
          this.hasValidationError = false;
          this.setButtonError(false);
        } else {
          labelEl.textContent = this.hasValidationError ? 'Please Select Options' : 'Select Options';
        }

        btn?.classList.toggle('is-not-ready', isNotReady);
      }

      // allRequiredSatisfied() {
      //   // 1. Check visible required add-on details panels
      //   const requiredPanels = [...this.querySelectorAll(
      //     'details.add-ons-options__input:not(.hidden)[data-required="true"]'
      //   )];
      //   for (const panel of requiredPanels) {
      //     const radio = panel.querySelector('input[type="radio"]:checked');
      //     const engraving = panel.querySelector('input.product-engraving');
      //     if (radio) continue;
      //     if (engraving && engraving.value.trim()) continue;
      //     return false;
      //   }

      //   // 2. Check required customization modal groups (data-customization-required on the section div)
      //   const requiredCustSections = document.querySelectorAll(
      //     '.customization-option-section[data-customization-required="true"]'
      //   );
      //   for (const section of requiredCustSections) {
      //     const hasCustomizationRadio = section.querySelector('[data-customization-input]');
      //     if (hasCustomizationRadio) {
      //       if (!section.querySelector('[data-customization-input]:checked')) return false;
      //       continue;
      //     }
      //     const detailsInSection = section.querySelector('details[data-engraving-root]');
      //     if (detailsInSection) {
      //       if (this.isEngravingDetailsSkipped(detailsInSection)) continue;
      //       const engravingInput = section.querySelector('input.product-engraving');
      //       if (engravingInput && !engravingInput.value.trim()) return false;
      //       continue;
      //     }
      //     const engravingInput = section.querySelector('input.product-engraving');
      //     if (engravingInput && !engravingInput.value.trim()) return false;
      //   }

      //   return true;
      // }
        allRequiredSatisfied() {
        // Regular required add-on fields
        const requiredPanels = [
          ...this.querySelectorAll(
            'details.add-ons-options__input:not(.hidden)[data-required="true"]'
          ),
        ];

        for (const panel of requiredPanels) {
          const radio = panel.querySelector(
            'input[type="radio"]:checked'
          );

          if (radio) continue;

          const engraving = panel.querySelector(
            'input.product-engraving'
          );

          const isSkipped =
            this.isEngravingDetailsSkipped(panel);

          const hasText = Boolean(
            engraving && engraving.value.trim()
          );

          if (isSkipped || hasText) continue;

          return false;
        }

        // Required options inside the customization modal
        const requiredSections = this.querySelectorAll(
          '.customization-option-section' +
          '[data-customization-required="true"]'
        );

        for (const section of requiredSections) {
          const radio = section.querySelector(
            '[data-customization-input]'
          );

          if (radio) {
            if (
              !section.querySelector(
                '[data-customization-input]:checked'
              )
            ) {
              return false;
            }

            continue;
          }

          const details = section.querySelector(
            'details[data-engraving-root]'
          );

          const engraving = section.querySelector(
            'input.product-engraving'
          );

          if (details || engraving) {
            const isSkipped = details
              ? this.isEngravingDetailsSkipped(details)
              : false;

            const hasText = Boolean(
              engraving && engraving.value.trim()
            );

            if (!isSkipped && !hasText) {
              return false;
            }
          }
        }

        return true;
      }

      updatePrice() {
        // 1. Sync internal base price with current variant
        this.setBasePriceFromVariantId();

        // 2. Resolve main product price (fallback to attribute if JSON lookup failed)
        let basePrice = this.basePriceCents;
        if (Number.isNaN(basePrice)) {
          const priceEls = this.getPriceElements();
          const attrEl = priceEls[0]?.dataset?.basePrice != null
            ? priceEls[0]
            : priceEls[0]?.querySelector?.('[data-product-price]') || priceEls[0];
          basePrice = parseInt(attrEl?.dataset?.basePrice || '', 10);
        }

        if (Number.isNaN(basePrice)) return;

        // 3. Sum all selected add-on prices
        let addonPrice = 0;
        this.querySelectorAll(
          'details.add-ons-options__input:not(.hidden) input[type="radio"]:checked'
        ).forEach((radio) => {
          const p = parseInt(radio.getAttribute('data-addon-price') || '0', 10);
          if (!Number.isNaN(p)) addonPrice += p;
        });

        this.querySelectorAll(
          'details.add-ons-options__input:not(.hidden) input.product-engraving'
        ).forEach((input) => {
          const details = input.closest('details.add-ons-options__input');
          if (details && this.isEngravingDetailsSkipped(details)) return;
          if (!input.value.trim()) return;
          const p = parseInt(input.getAttribute('data-addon-price') || '0', 10);
          if (!Number.isNaN(p)) addonPrice += p;
        });

        document.querySelectorAll('[data-customization-input]:checked').forEach((radio) => {
          const p = parseInt(radio.getAttribute('data-addon-price') || '0', 10);
          if (!Number.isNaN(p)) addonPrice += p;
        });

        // 4. Calculate display values
        const hasFilledEngraving = [
          ...this.querySelectorAll('details.add-ons-options__input:not(.hidden) input.product-engraving'),
        ].some((el) => {
          const details = el.closest('details.add-ons-options__input');
          if (details && this.isEngravingDetailsSkipped(details)) return false;
          return el.value.trim().length > 0;
        });
        const anySelected =
          this.querySelector('details.add-ons-options__input:not(.hidden) input[type="radio"]:checked') ||
          document.querySelector('[data-customization-input]:checked') ||
          hasFilledEngraving;

        // ATC button shows 0 unless something is selected
        // const totalCents = anySelected ? (basePrice + addonPrice) : 0;
        const totalCents = basePrice + addonPrice;
        
        // Main display shows variant price + addons (always >= basePrice)
        const mainDisplayPrice = basePrice + addonPrice;

        this.updateDisplayedPrice(mainDisplayPrice, totalCents);
      }

      isPanelVisible(detailsEl) {
        return !detailsEl.classList.contains('hidden');
      }

      /** When skip is checked, do not price or persist engraving text. */
      isEngravingDetailsSkipped(detailsEl) {
        if (!detailsEl) return false;
        const box = detailsEl.querySelector('.engraving-skip');
        if (box) return box.checked;
        const section = detailsEl.closest?.('.customization-option-section--engraving');
        const out = section && section.querySelector('.engraving-skip');
        if (out) return out.checked;
        return false;
      }

      /** Line item property key: matches Liquid `name` on radios / engraving input. */
      getPropertyKeyFromDetails(detailsEl) {
        const radio = detailsEl.querySelector('input[type="radio"]');
        if (radio) {
          const name = (radio.name || '').trim();
          return name;
        }
        const textInput = detailsEl.querySelector('input.product-engraving');
        if (textInput) {
          return (textInput.name || '').trim();
        }
        return '';
      }

      getEngravingCharLimit(input) {
        const raw =
          input.getAttribute('maxlength') ||
          input.getAttribute('data-character-limit') ||
          input.dataset.characterLimit ||
          '';
        const limit = parseInt(raw, 10);
        return Number.isNaN(limit) || limit < 1 ? 0 : limit;
      }

      clampEngravingValue(input) {
        const limit = this.getEngravingCharLimit(input);
        if (limit > 0 && input.value.length > limit) {
          input.value = input.value.slice(0, limit);
        }
      }

      refreshConditionalPanels() {
        const conditionalPanels = this.querySelectorAll('details.add-ons-options__input[data-option-index]');
        const allRadios = this.querySelectorAll('input[type="radio"]');

        conditionalPanels.forEach((panel) => {
          const idx = panel.dataset.optionIndex;
          const optName = panel.dataset.optionName;
          let show = false;

          allRadios.forEach((radio) => {
            if (!radio.checked) return;
            const parentIdx = radio.dataset.conditionalIndex;
            const opts = (radio.dataset.conditionalOptions || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            if (parentIdx === idx && opts.includes(optName)) {
              show = true;
            }
          });

          panel.classList.toggle('hidden', !show);
          if (!show) {
            this.setDetailsError(panel, false);
            panel.querySelectorAll('input[type="radio"]').forEach((r) => {
              r.checked = false;
            });
            const sel = panel.querySelector('summary .selected-value');
            if (sel) sel.textContent = '';
          }
        });
      }

      updateSummaryForDetails(detailsEl) {
        const sel = detailsEl.querySelector('summary .selected-value');
        if (!sel) return;
        const radio = detailsEl.querySelector('input[type="radio"]:checked');
        const textInput = detailsEl.querySelector('input.product-engraving');
        if (radio) {
          let text = String(radio.value || '').trim();
          const isDropdown = detailsEl.classList.contains('add-ons-options__input-dropdown');
          if (isDropdown && text) {
            const addonCents = parseInt(radio.getAttribute('data-addon-price') || '0', 10);
            if (!Number.isNaN(addonCents) && addonCents > 0) {
              let money = this.formatMoney(addonCents).trim();
              if (money.endsWith('.00')) money = money.slice(0, -3);
              if (money.endsWith(',00')) money = money.slice(0, -3);
              text = `${text} (+${money})`;
            }
          }
          sel.textContent = text;
        } else if (textInput) {
          if (this.isEngravingDetailsSkipped(detailsEl)) {
            sel.textContent = '';
          } else {
            sel.textContent = textInput.value.trim();
          }
        } else {
          sel.textContent = '';
        }
      }

      updateAllSummaries() {
        this.querySelectorAll('details.add-ons-options__input').forEach((d) => {
          if (this.isPanelVisible(d)) {
            this.updateSummaryForDetails(d);
          }
        });
      }

      ensureMobileBackdrop() {
        let backdrop = this.querySelector('.add-ons-mobile-backdrop');
        if (!backdrop) {
          backdrop = document.createElement('button');
          backdrop.type = 'button';
          backdrop.className = 'add-ons-mobile-backdrop';
          backdrop.setAttribute('aria-label', 'Close options');
          backdrop.setAttribute('tabindex', '-1');
          this.appendChild(backdrop);
        }
        this.mobileBackdrop = backdrop;
        this.mobileBackdrop.addEventListener('click', this.onMobileBackdropClick);
      }

      /**
       * Customization modal inlines `<details data-addon-sheet open>` (engraving). Those must not
       * activate the PDP bottom sheet / backdrop or get closed by PDP outside-click handlers.
       */
      isAddonSheetInCustomizationModal(el) {
        return Boolean(el?.closest?.('.customization-modal-overlay'));
      }

      /** Open PDP accordion sheets only (excludes embedded customization modal UI). */
      queryPdpOpenAddonSheets() {
        return Array.from(this.querySelectorAll(ADD_ON_MOBILE_SHEET_OPEN)).filter(
          (el) => !this.isAddonSheetInCustomizationModal(el)
        );
      }

      syncMobileDropdownState() {
        const hasOpenSheet = this.queryPdpOpenAddonSheets().length > 0;
        const shouldActivate = window.matchMedia('(max-width: 749px)').matches && hasOpenSheet;

        this.classList.toggle('add-on-mobile-sheet-active', shouldActivate);

        const anyActive = document.querySelector('product-add-ons.add-on-mobile-sheet-active');
        document.body.classList.toggle('addon-dropdown-mobile-open', Boolean(anyActive));
      }

      applyMobileDropdownLayout(detailsEl) {
        if (!detailsEl) return;
        const content = detailsEl.querySelector(':scope > div');
        const summary = detailsEl.querySelector(':scope > summary');
        if (!content || !summary) return;

        if (!window.matchMedia('(max-width: 749px)').matches || !detailsEl.open) {
          content.style.maxHeight = '';
          content.style.overflowY = '';
          content.style.setProperty('-webkit-overflow-scrolling', '');
          return;
        }

        const summaryHeight = Math.ceil(summary.getBoundingClientRect().height || 0);
        const maxHeight = Math.max(120, Math.round(window.innerHeight * 0.7) - summaryHeight);
        content.style.maxHeight = `${maxHeight}px`;
        content.style.overflowY = 'auto';
        content.style.setProperty('-webkit-overflow-scrolling', 'touch');
      }

      onDropdownToggle(event) {
        const details = event.target;
        if (!details?.matches?.(ADD_ON_OPTION_SHEET_DETAILS)) return;

        if (details.open) {
          const inModal = this.isAddonSheetInCustomizationModal(details);
          this.querySelectorAll(ADD_ON_MOBILE_SHEET_OPEN).forEach((other) => {
            if (other === details) return;
            if (this.isAddonSheetInCustomizationModal(other) !== inModal) return;
            other.removeAttribute('open');
          });
        }

        this.applyMobileDropdownLayout(details);
        this.syncMobileDropdownState();
      }

      onDocumentClick(event) {
        const target = event.target;
        if (!target) return;

        const clickedGuideTrigger = target.closest?.('[data-variant-popup-trigger]');
        if (clickedGuideTrigger && this.contains(clickedGuideTrigger)) {
          this.queryPdpOpenAddonSheets().forEach((detailsEl) => {
            detailsEl.removeAttribute('open');
          });
          this.syncMobileDropdownState();
          return;
        }

        const pdpOpenSheets = this.queryPdpOpenAddonSheets();
        if (pdpOpenSheets.length === 0) return;

        // Close open PDP option sheets when clicking anywhere outside this product-add-ons block
        // OR inside it but outside an open sheet panel/summary.
        const clickedInsideComponent = this.contains(target);
        const clickedInsidePdpOpenSheet = pdpOpenSheets.some((d) => d.contains(target));
        if (clickedInsideComponent && clickedInsidePdpOpenSheet) return;

        pdpOpenSheets.forEach((detailsEl) => {
          detailsEl.removeAttribute('open');
        });
        this.syncMobileDropdownState();
      }

      onDropdownOptionClick(event) {
        const detailsEl = event.target?.closest?.(ADD_ON_MOBILE_SHEET_OPEN);
        if (!detailsEl) return;

        let inputEl = null;
        if (event.target?.matches?.('input[type="radio"]')) {
          inputEl = event.target;
        } else {
          const labelEl = event.target?.closest?.('label[for]');
          if (labelEl) {
            const forId = labelEl.getAttribute('for') || '';
            if (forId) {
              inputEl = detailsEl.querySelector(`input[type="radio"][id="${forId}"]`);
            }
          }
        }

        if (!inputEl || inputEl.type !== 'radio') return;
        if (!inputEl.checked) return;

        // Clicking the already-selected option should close the dropdown.
        requestAnimationFrame(() => {
          if (detailsEl.hasAttribute('open')) {
            detailsEl.removeAttribute('open');
            this.syncMobileDropdownState();
          }
        });
      }

      onViewportResize() {
        this.querySelectorAll(ADD_ON_MOBILE_SHEET_OPEN).forEach((detailsEl) => {
          this.applyMobileDropdownLayout(detailsEl);
        });
        this.syncMobileDropdownState();
      }

      onMobileBackdropClick(event) {
        event.preventDefault();
        this.queryPdpOpenAddonSheets().forEach((detailsEl) => {
          detailsEl.removeAttribute('open');
        });
        this.syncMobileDropdownState();
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape') return;
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const pdpOpen = this.queryPdpOpenAddonSheets();
        if (pdpOpen.length === 0) return;

        pdpOpen.forEach((detailsEl) => {
          detailsEl.removeAttribute('open');
        });
        this.syncMobileDropdownState();
      }

      onRadioChange(event) {
        if (event.target?.type !== 'radio') return;
        this.hasValidationError = false;
        this.setButtonError(false);
        this.refreshConditionalPanels();
        this.updateButtonState();
        const detailsEl = event.target.closest('details.add-ons-options__input');
        if (detailsEl) {
          this.updateSummaryForDetails(detailsEl);
          if (event.target.checked) {
            this.clearFieldError(detailsEl);
            detailsEl.removeAttribute('open');
          }
        }
        this.querySelectorAll('details.add-ons-options__input').forEach((d) => {
          if (this.isPanelVisible(d)) {
            this.updateSummaryForDetails(d);
          }
        });
        this.updatePrice();
      }

      onEngravingInput(event) {
        if (!event.target?.classList?.contains('product-engraving')) return;
        this.hasValidationError = false;
        this.setButtonError(false);
        const input = event.target;
        this.clampEngravingValue(input);
        const detailsEl = input.closest('details.add-ons-options__input');
        if (detailsEl) {
          this.updateSummaryForDetails(detailsEl);
          if (input.value.trim()) {
            this.clearFieldError(detailsEl);
          }
          input.setCustomValidity('');
        }
        this.updateButtonState();
        this.updatePrice();
      }

      setDetailsError(detailsEl, hasError) {
        if (!detailsEl) return;
        detailsEl.classList.toggle('is-error', Boolean(hasError));
      }

      setButtonError(hasError) {
        const btn = this.querySelector('.add-on__add-cart');
        if (!btn) return;
        btn.classList.toggle('is-error', Boolean(hasError));
      }

      clearErrors() {
        this.querySelectorAll('details.add-ons-options__input').forEach((el) => {
          this.setDetailsError(el, false);
        });
      }

      clearFieldError(detailsEl) {
        this.setDetailsError(detailsEl, false);
      }

      validate() {
        this.clearErrors();
        this.hasValidationError = false;
        this.setButtonError(false);
        let ok = true;

        this.querySelectorAll('details.add-ons-options__input').forEach((detailsEl) => {
          if (!this.isPanelVisible(detailsEl)) return;
          if (detailsEl.dataset.required !== 'true') return;

          const hasRadio = detailsEl.querySelector('input[type="radio"]');
          const textInput = detailsEl.querySelector('input.product-engraving');

          if (hasRadio) {
            if (!detailsEl.querySelector('input[type="radio"]:checked')) {
              ok = false;
              this.setDetailsError(detailsEl, true);
            }
          } else if (textInput && detailsEl.dataset.required === 'true') {
            if (this.isEngravingDetailsSkipped(detailsEl)) {
              // skip allows empty text
            } else if (!textInput.value.trim()) {
              ok = false;
              this.setDetailsError(detailsEl, true);
            }
          }
        });

        // Check required customization modal groups
        const anyRequiredCustMissing = Array.from(
          document.querySelectorAll(
            '.customization-option-section[data-customization-required="true"]'
          )
        ).some((section) => {
          const hasCustomizationRadio = section.querySelector('[data-customization-input]');
          if (hasCustomizationRadio) {
            return !section.querySelector('[data-customization-input]:checked');
          }
          const d = section.querySelector('details[data-engraving-root]');
          if (d && this.isEngravingDetailsSkipped(d)) return false;
          const engravingInput = section.querySelector('input.product-engraving');
          return engravingInput ? !engravingInput.value.trim() : false;
        });

        if (anyRequiredCustMissing) {
          ok = false;
          // Mark the trigger button with an inline error (do NOT open the modal)
          document.querySelectorAll('[data-customization-trigger]').forEach((btn) => {
            btn.classList.add('is-error');
          });
        }

        this.querySelectorAll('input.product-engraving').forEach((textInput) => {
          const detailsEl = textInput.closest('details.add-ons-options__input');
          if (!detailsEl || !this.isPanelVisible(detailsEl)) return;
          if (this.isEngravingDetailsSkipped(detailsEl)) {
            textInput.setCustomValidity('');
            return;
          }
          this.clampEngravingValue(textInput);
          const limit = this.getEngravingCharLimit(textInput);
          if (limit <= 0) {
            textInput.setCustomValidity('');
            return;
          }
          if (textInput.value.length > limit) {
            ok = false;
            this.setDetailsError(detailsEl, true);
            textInput.setCustomValidity(`Maximum ${limit} characters.`);
            textInput.reportValidity();
          } else {
            textInput.setCustomValidity('');
          }
        });

        this.hasValidationError = !ok;
        this.setButtonError(!ok);
        this.updateButtonState();
        return ok;
      }

      // Matches Liquid's `handleize`: ASCII-lowercase, non-alphanumerics -> '-', collapsed, trimmed.
      // This slug is used for hidden-property keys consumed by the Cart Transform function.
      handleize(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      // Sum surcharges from visible, selected add-ons (radios + engraving text input with value).
      // Returns { cents, keys[], perAddon: [{ slug, label, variantId, cents, value }] }.
      // Used both for the hidden `_addon_surcharge_cents` line property and the preview price.
      collectAddonSurcharges() {
        const result = { cents: 0, keys: [], perAddon: [] };

        this.querySelectorAll('details.add-ons-options__input').forEach((detailsEl) => {
          if (!this.isPanelVisible(detailsEl)) return;
          const label = this.getPropertyKeyFromDetails(detailsEl);
          if (!label) return;

          const radio = detailsEl.querySelector('input[type="radio"]:checked');
          if (radio) {
            const price = parseInt(radio.getAttribute('data-addon-price') || '0', 10) || 0;
            if (price <= 0) return;
            const slug = this.handleize(label);
            const vid = radio.getAttribute('data-variant-id') || radio.dataset.variantId || '';
            result.cents += price;
            result.keys.push(slug);
            result.perAddon.push({
              slug,
              label,
              value: String(radio.value || ''),
              variantId: vid ? String(vid) : '',
              cents: price,
            });
            return;
          }

          const textInput = detailsEl.querySelector('input.product-engraving');
          if (textInput) {
            if (this.isEngravingDetailsSkipped(detailsEl)) return;
            const v = textInput.value.trim();
            if (!v) return;
            const price = parseInt(textInput.getAttribute('data-addon-price') || '0', 10) || 0;
            if (price <= 0) return;
            const slug = this.handleize(label);
            const vid = textInput.getAttribute('data-variant-id') || textInput.dataset.variantId || '';
            result.cents += price;
            result.keys.push(slug);
            result.perAddon.push({
              slug,
              label,
              value: v,
              variantId: vid ? String(vid) : '',
              cents: price,
            });
          }
        });

        this.querySelectorAll('[data-customization-input]:checked').forEach((radio) => {
          const label = (radio.name || '').trim();
          if (!label) return;
          const price = parseInt(radio.getAttribute('data-addon-price') || '0', 10) || 0;
          if (price <= 0) return;
          const slug = this.handleize(label);
          const vid = radio.getAttribute('data-variant-id') || radio.dataset.variantId || '';
          
          result.cents += price;
          result.keys.push(slug);
          result.perAddon.push({
            slug,
            label,
            value: String(radio.value || ''),
            variantId: vid ? String(vid) : '',
            cents: price,
          });
        });

        return result;
      }

      /**
       * Legacy (before a7e9662): parent line properties for multi-line `/cart/add.js`.
       */
      collectMainLineProperties(bundleGroupId) {
        const properties = {
          _has_bundle_addons: '1',
          _bundle_group_id: String(bundleGroupId),
        };

        this.querySelectorAll('details.add-ons-options__input').forEach((detailsEl) => {
          if (!this.isPanelVisible(detailsEl)) return;
          const key = this.getPropertyKeyFromDetails(detailsEl);
          if (!key) return;

          const radio = detailsEl.querySelector('input[type="radio"]:checked');
          const textInput = detailsEl.querySelector('input.product-engraving');

          if (radio) {
            properties[key] = String(radio.value);
          } else if (textInput) {
            if (this.isEngravingDetailsSkipped(detailsEl)) {
              // no properties from skipped engraving
            } else {
              const v = textInput.value.trim();
              if (v) {
                properties[key] = String(v);
                const fontInp = detailsEl.querySelector('input.product-engraving-font');
                const fk = (fontInp && fontInp.name) ? String(fontInp.name).trim() : '';
                const fv = (fontInp && fontInp.value) ? String(fontInp.value).trim() : '';
                if (fk && fv) properties[fk] = fv;
              }
            }
          }
        });

        this.querySelectorAll('[data-customization-input]:checked').forEach((radio) => {
          const label = (radio.name || '').trim();
          if (label) {
            properties[label] = String(radio.value || '');
          }
        });

        return properties;
      }

      /**
       * Legacy: one cart line per selected add-on variant (pre-commit behavior).
       */
      collectAddonLineItems(bundleGroupId) {
        const items = [];

        this.querySelectorAll('details.add-ons-options__input').forEach((detailsEl) => {
          if (!this.isPanelVisible(detailsEl)) return;
          const key = this.getPropertyKeyFromDetails(detailsEl);
          if (!key) return;

          const radio = detailsEl.querySelector('input[type="radio"]:checked');
          if (radio) {
            const vid = radio.getAttribute('data-variant-id') || radio.dataset.variantId;
            if (vid) {
              items.push({
                id: Number(vid),
                quantity: 1,
                properties: {
                  _bundle_parent_group_id: String(bundleGroupId),
                  _bundle_option_label: key,
                },
              });
            }
          }

          const textInput = detailsEl.querySelector('input.product-engraving');
          if (textInput) {
            if (this.isEngravingDetailsSkipped(detailsEl)) {
              // skip: no add-on line
            } else {
              const vid = textInput.getAttribute('data-variant-id') || textInput.dataset.variantId;
              const v = textInput.value.trim();
              if (vid && v) {
                items.push({
                  id: Number(vid),
                  quantity: 1,
                  properties: {
                    _bundle_parent_group_id: String(bundleGroupId),
                    _bundle_option_label: key,
                  },
                });
              }
            }
          }
        });

        this.querySelectorAll('[data-customization-input]:checked').forEach((radio) => {
          const label = (radio.name || '').trim();
          if (!label) return;
          const vid = radio.getAttribute('data-variant-id') || radio.dataset.variantId;
          if (vid) {
            items.push({
              id: Number(vid),
              quantity: 1,
              properties: {
                _bundle_parent_group_id: String(bundleGroupId),
                _bundle_option_label: label,
              },
            });
          }
        });

        return items;
      }

      /**
       * Combined cart line (a7e9662): single parent line + `_addon_surcharge_cents` for Cart Transform.
       */
      collectCombinedLineItemProperties(surcharges) {
        const properties = {};

        this.querySelectorAll('details.add-ons-options__input').forEach((detailsEl) => {
          if (!this.isPanelVisible(detailsEl)) return;
          const key = this.getPropertyKeyFromDetails(detailsEl);
          if (!key) return;

          const radio = detailsEl.querySelector('input[type="radio"]:checked');
          const textInput = detailsEl.querySelector('input.product-engraving');

          if (radio) {
            properties[key] = String(radio.value);
          } else if (textInput) {
            if (this.isEngravingDetailsSkipped(detailsEl)) {
              // no text properties when skip
            } else {
              const v = textInput.value.trim();
              if (v) {
                properties[key] = String(v);
                const fontInp = detailsEl.querySelector('input.product-engraving-font');
                const fk = (fontInp && fontInp.name) ? String(fontInp.name).trim() : '';
                const fv = (fontInp && fontInp.value) ? String(fontInp.value).trim() : '';
                if (fk && fv) {
                  properties[fk] = fv;
                }
              }
            }
          }
        });

        this.querySelectorAll('[data-customization-input]:checked').forEach((radio) => {
          const label = (radio.name || '').trim();
          if (label) {
            properties[label] = String(radio.value || '');
          }
        });

        if (surcharges.cents > 0) {
          properties._addon_surcharge_cents = String(surcharges.cents);
          properties._addon_keys = surcharges.keys.join(',');

          surcharges.perAddon.forEach((addon) => {
            if (!addon.slug) return;
            properties[`_addon_${addon.slug}_price_cents`] = String(addon.cents);
            if (addon.variantId) {
              properties[`_addon_${addon.slug}_variant_id`] = addon.variantId;
            }
          });
        }

        return properties;
      }

      onAddToCartClick(event) {
        event.preventDefault();
        if (!this.validate()) return;

        const form = this.getProductForm();
        if (!form) {
          console.error('product-add-ons: form not found', this.formId);
          return;
        }

        const variantInput = form.querySelector('[name="id"]');
        if (!variantInput || variantInput.disabled || !variantInput.value) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-add-ons',
              errors: 'Unavailable',
            });
          }
          return;
        }

        const mainVariantId = Number(variantInput.value);
        const qtyInput = form.querySelector('input[name="quantity"]');
        const quantity = Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);

        let items;
        if (this.combineAddonProducts) {
          const surcharges = this.collectAddonSurcharges();
          const mainProperties = this.collectCombinedLineItemProperties(surcharges);
          items = [{ id: mainVariantId, quantity, properties: mainProperties }];
        } else {
          const bundleGroupId = Date.now();
          const mainProperties = this.collectMainLineProperties(bundleGroupId);
          const addonItems = this.collectAddonLineItems(bundleGroupId);
          items = [{ id: mainVariantId, quantity, properties: mainProperties }, ...addonItems];
        }

        const payload = { items };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement?.(document.activeElement);
        }

        this.addButton?.classList.add('loading');
        this.addButton?.setAttribute('disabled', 'disabled');

        fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        })
          .then((response) => response.json())
          .then((response) => {
            window.location = window.routes?.cart_url || '/cart';
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.addButton?.classList.remove('loading');
            this.addButton?.removeAttribute('disabled');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
          });
      }
    }
  );
}
